import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMLegalMenuBarContainer } from "../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { CRMComposeEmailForm } from "../../components/CRM/CRMComposeEmail/CRMComposeEmailForm/CRMComposeEmailForm";
import { Padding } from "../../components/BuildingBlocks/Padding";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMTriageInbox } from "../../components/Complex/CRMTriageInbox/CRMTriageInbox";
import { CRMBlockScrollCacheView } from "../../components/CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { CRMBlock } from "../../components/CRM/CRMBlock/CRMBlock/CRMBlock";
import { CRMLegalInboxRightPane } from "../../components/CRM/CRMLegalInboxRightPane/CRMLegalInboxRightPane";
import { CRMWeeklyUpdatesBlock } from "../../components/CRM/CRMInboxBlocks/CRMWeeklyUpdatesBlock";

const CRMLegalTriageInboxPage = (props: TContainerStateProps): JSX.Element =>
    <CRMPageWrap>
        {/* HEADER */}
        <CRMLegalMenuBarContainer {...props} />

        {/* BODY */}
        <Padding
            type="custom" 
            spacing={`${CRMSpacing.X_LARGE} ${CRMSpacing.X_LARGE} 0px`}
        >
            <SpacingRow
                spacing={CRMSpacing.X_LARGE}
                alignItems="flex-start"
            >
                {/* BLOCK - TRIAGE (left pane with select-options swap) */}
                <CRMBlock
                    mode="select-options"
                    startingWidth={700}
                    selectedOption={props.state.forms.triage_page.left_pane_mode}
                    optionGroups={[{
                        name: "Left pane",
                        options: {
                            triage_inbox: { icon: "triangle", title: "Triage" },
                            weekly_updates: { icon: "send-email", title: "Weekly Updates" },
                        },
                    }]}
                    onChange={(name) => {
                        if (name === "triage_inbox" || name === "weekly_updates") {
                            props.dispatch({
                                type: "TRIAGE_SET_LEFT_PANE_MODE",
                                payload: name,
                            });
                        }
                    }}
                >
                    {/* Render conditionally rather than via CRMBlockScrollCacheView's display:none
                        wrapper — CRMTriageInbox has a position:sticky footer that needs its parent
                        block to be its own positioning context. The extra wrapper div introduced
                        by CRMBlockScrollCacheView (.crm-block-view + Background) pulled the
                        sticky footer to the page-bottom instead of the block-bottom. */}
                    {props.state.forms.triage_page.left_pane_mode === "weekly_updates"
                        ? <CRMWeeklyUpdatesBlock
                            openCaseId={props.state.forms.triage_page.weekly_updates_open_case_id}
                            onPreloadDraft={(payload) => props.dispatch({
                                type: "WEEKLY_UPDATES_PRELOAD_COMPOSE",
                                payload,
                            })}
                            onClosePanel={() => props.dispatch({
                                type: "WEEKLY_UPDATES_CLOSE_PANEL",
                                payload: null,
                            })}
                        />
                        :
                    <CRMTriageInbox
                        unassignedForm={props.state.forms.triage_page.unassigned}
                        expiredForm={props.state.forms.triage_page.expired}
                        staffInboxesForm={props.state.forms.triage_page.staff_inboxes}
                        staffInboxEmailsForm={props.state.forms.triage_page.staff_inbox_emails}
                        triageTypeVisible={props.state.forms.triage_page.active_type_visible}
                        onChangeTriageTypeVisible={(payload) => props.dispatch({
                            type: "TRIAGE_ACTIVE_TYPE_VISIBLE_CHANGE",
                            payload,
                        })}
                        onChangeSearchCaseQuery={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_SEARCH_CASE`,
                            payload,
                        })}
                        onOpenEmail={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_OPEN_EMAIL`,
                            payload,
                        })}
                        onLoadMore={(triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_LOAD_MORE`,
                        })}
                        onChangeImportanceBand={(band) => props.dispatch({
                            type: "TRIAGE_UNASSIGNED_SET_BAND",
                            payload: band,
                        })}
                        onArchive={(payload, triageType) => props.dispatch({
                            type: triageType === "STAFF_INBOXES"
                                ? "TRIAGE_STAFF_INBOXES_ARCHIVE"
                                : "TRIAGE_UNASSIGNED_ARCHIVE",
                            payload
                        })}
                        onResolve={(payload) => props.dispatch({
                            type: `TRIAGE_EXPIRED_RESOLVE`,
                            payload
                        })}
                        onChangeChaseModeForm={(payload) => props.dispatch({
                            type: "TRIAGE_EXPIRED_RESOLVE_FORM_UPDATE_CHASE_MODE_FORM",
                            payload,
                        })}
                        onBulkArchive={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_BULK_ARCHIVE`,
                            payload
                        })}
                        onAssignToPerson={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_ASSIGN_TO_PERSON`,
                            payload
                        })}
                        onAssignToCaseAndMe={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_ASSIGN_TO_CASE_AND_ME`,
                            payload
                        })}
                        onAssignToCaseAndStaff={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_ASSIGN_TO_CASE_AND_STAFF`,
                            payload
                        })}
                        onAssignToCaseAndHandler={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_ASSIGN_TO_CASE_AND_HANDLER`,
                            payload
                        })}
                        onAssignToCaseAndResolve={(payload, triageType) => props.dispatch({
                            type: `TRIAGE_${triageType}_ASSIGN_TO_CASE_AND_RESOLVE`,
                            payload
                        })}
                        onSelectStaffUser={(payload) => props.dispatch({
                            type: "TRIAGE_STAFF_INBOXES_SELECT_USER",
                            payload,
                        })}
                        onToggleAway={(payload) => props.dispatch({
                            type: "TRIAGE_STAFF_INBOXES_TOGGLE_AWAY",
                            payload,
                        })}
                        onReply={(payload) => props.dispatch({
                            type: "CRM_COMPOSE_EMAIL_REPLY",
                            payload
                        })}
                        onForward={(payload) => props.dispatch({
                            type: "CRM_COMPOSE_EMAIL_FORWARD",
                            payload
                        })}
                        onFileAttachments={(payload) => {
                            const caseId = payload.children.email.currently_assigned_case_id;
                            if (!caseId) return;
                            props.dispatch({
                                type: "CRM_COMPOSE_FILE_ATTACHMENTS",
                                payload: { email: payload, case_id: caseId },
                            });
                        }}
                    />
                    }
                </CRMBlock>

                <CRMLegalInboxRightPane
                    assignmentState={props.state.forms.global_legal}
                    onCancel={() => props.dispatch({
                        type: "CRM_CLOSE_FILE_ATTACHMENTS",
                        payload: null,
                    })}
                    onCommit={(commit) => props.dispatch({
                        type: "CRM_FILE_ATTACHMENTS_COMMIT",
                        payload: {
                            case_id: props.state.forms.global_legal.file_attachments_assignment_case_id,
                            email_id: props.state.forms.global_legal.file_attachments_assignment_email.children.email.id,
                            commit,
                        },
                    })}
                >
                    {/* BLOCK - COMPOSE EMAIL */}
                    <CRMBlock
                        mode="fixed-option"
                        fixedOption={{
                            icon: "send-email",
                            title: "Compose Email"
                        }}
                        optionGroups={[]}
                    >
                        <CRMBlockScrollCacheView isShowing={true}>
                            <CRMComposeEmailForm
                                apiUrl={env.REACT_APP_API_URL}
                                templatesForm={props.state.forms.email_templates_form}
                                closeMode="clear-only"
                                composingUser={sessionUser}
                                additionalSendToEmailAddresses={[]}
                                attachedEmail={props.state.forms.global_legal.attached_email}
                                onChangeSearchCaseValue={(payload) => props.dispatch({
                                    type: "CRM_COMPOSE_EMAIL_TYPE_CASE_SEARCH_TEXT",
                                    payload,
                                })}
                                form={props.state.forms.global_legal.compose_email}
                                onSelectCase={(payload) => props.dispatch({
                                    type: "CRM_SET_CASE_FOR_COMPOSE_EMAIL_FORM",
                                    payload,
                                })}
                                onChange={(payload) => props.dispatch({
                                    type: "CRM_UPDATE_COMPOSE_EMAIL_FORM",
                                    payload,
                                })}
                                onSubmit={(payload) => props.dispatch({
                                    type: "CRM_SEND_NEW_EMAIL",
                                    payload,
                                })}
                                onReply={(payload) => props.dispatch({
                                    type: "CRM_SEND_REPLY_EMAIL",
                                    payload,
                                })}
                                onForward={(payload) => props.dispatch({
                                    type: "CRM_SEND_FORWARD_EMAIL",
                                    payload,
                                })}
                                onClear={() => props.dispatch({
                                    type: "CRM_RESET_COMPOSE_EMAIL_FORM",
                                    payload: null,
                                })}
                            />
                        </CRMBlockScrollCacheView>
                    </CRMBlock>
                </CRMLegalInboxRightPane>
            </SpacingRow>
        </Padding>
    </CRMPageWrap>
;

export default CRMLegalTriageInboxPage;
